import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from "react";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useAuth } from "./AuthContext";

type Plan = "free" | "familiar" | "enterprise";

type Limits = {
  maxFamilies: number;
  maxUsers: number; // usuarios con cuenta
  maxMembers: number; // miembros sin cuenta
  maxActivities?: number; // por familia
  features: {
    allowGrace: boolean;
    allowPoints: boolean;
  };
};

type SubscriptionContextType = {
  plan: Plan;
  limits: Limits;
  currentPeriodEnd?: Date | null;
};

// Límites por defecto como fallback (solo si falla la carga desde Firestore)
const defaultPlanLimits: Record<Plan, Limits> = {
  free: {
    maxFamilies: 1,
    maxUsers: 1,
    maxMembers: 5,
    maxActivities: 5,
    features: { allowGrace: false, allowPoints: false },
  },
  familiar: {
    maxFamilies: 3,
    maxUsers: 6,
    maxMembers: 14,
    maxActivities: undefined,
    features: { allowGrace: true, allowPoints: true },
  },
  enterprise: {
    maxFamilies: 9999,
    maxUsers: 9999,
    maxMembers: 9999,
    maxActivities: undefined,
    features: { allowGrace: true, allowPoints: true },
  },
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  const [plan, setPlan] = useState<Plan>("free");
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<Date | null>(null);
  const [planLimits, setPlanLimits] = useState<Record<Plan, Limits>>(defaultPlanLimits);
  const isMountedRef = useRef(true);

  const normalizePlan = (raw: unknown): Plan => {
    const v = String(raw || '').toLowerCase().trim();
    if (v === 'familiar') return 'familiar';
    if (v === 'family') return 'familiar';
    if (v === 'enterprise') return 'enterprise';
    return 'free';
  };

  // Cargar límites desde subscriptionPlans
  useEffect(() => {
    const loadPlanLimits = async () => {
      try {
        const plans: Plan[] = ['free', 'familiar', 'enterprise'];
        const newLimits: Record<Plan, Limits> = { ...defaultPlanLimits };
        
        for (const planKey of plans) {
          try {
            const planDoc = await getDoc(doc(db, 'subscriptionPlans', planKey));
            if (planDoc.exists()) {
              const planData = planDoc.data();
              const limits = planData?.limits || {};
              
              newLimits[planKey] = {
                maxFamilies: Number(limits.families) || defaultPlanLimits[planKey].maxFamilies,
                maxUsers: Number(limits.users) || defaultPlanLimits[planKey].maxUsers,
                maxMembers: Number(limits.members) || defaultPlanLimits[planKey].maxMembers,
                maxActivities: limits.activities === 0 || limits.activities >= 9999 ? undefined : Number(limits.activities),
                features: planData?.features || defaultPlanLimits[planKey].features,
              };
            }
          } catch (error) {
          }
        }
        
        setPlanLimits(newLimits);
      } catch (error) {
      }
    };
    
    loadPlanLimits();
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    if (!user?.uid) {
      setPlan("free");
      return;
    }

    // Iniciar listener inmediatamente para mejor sincronización
    
    const unsub = onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        if (!isMountedRef.current) return;
        const data: any = snap.exists() ? snap.data() : {};
        const p = data?.subscription?.plan;
        const newPlan = normalizePlan(p);
        setPlan(newPlan);

        if (!p) {
          // Fallback best-effort: si el doc de usuario no trae plan, intentar desde subscription_accounts
          void (async () => {
            try {
              const accSnap = await getDoc(doc(db, 'subscription_accounts', user.uid));
              const accPlan = accSnap.exists() ? (accSnap.data() as any)?.plan : null;
              const normalizedAccPlan = normalizePlan(accPlan);
              if (normalizedAccPlan && normalizedAccPlan !== 'free') {
                setPlan(normalizedAccPlan);
              }
            } catch {
            }
          })();
        }

        const end = data?.subscription?.currentPeriodEnd;
        const dt: Date | null = end?.seconds
          ? new Date(end.seconds * 1000)
          : end instanceof Date
          ? end
          : null;
        setCurrentPeriodEnd(dt);
      },
      (error) => {
        if (!isMountedRef.current) return;
        // Manejar errores de permisos silenciosamente
        setPlan("free");
      }
    );

    return () => {
      isMountedRef.current = false;
      if (unsub) {
        try {
          unsub();
        } catch (e) {
          // Ignore errors when listener is already closed
        }
      }
    };
  }, [user?.uid]);

  const value = useMemo<SubscriptionContextType>(
    () => ({ plan, limits: planLimits[plan], currentPeriodEnd }),
    [plan, currentPeriodEnd, planLimits]
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export const useSubscription = (): SubscriptionContextType => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error("useSubscription must be used within SubscriptionProvider");
  return ctx;
};
